/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import Button from "@mui/material/Button";
import SoftButton from "components/SoftButton";

// --- HELPERS ---
const createCostObject = () => ({
  totalAmount: "",
  constructionAmount: "",
  equipmentTotal: "",
  equipmentRequireInstallation: "",
  equipmentNoInstallation: "",
  toolsAmount: "",
  otherAmount: "",
});

const createItem = () => ({
  recordingDate: "",
  documentNumber: "",
  documentDate: "",
  description: "",
  correspondingAccount: "",
  note: "",
  costs: createCostObject(),
});

const defaultItems = [createItem(), createItem()];

function ProjectCostLedgerInfo({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();

  // --- QUẢN LÝ STATE THÔNG TIN CHUNG ---
  const [headerInfo, setHeaderInfo] = useState({
    company: "",
    companyAddress: "",
    year: today.getFullYear().toString(),
    projectName: "",
    constructionName: "",
    startDate: "",
    completionDate: "",
    totalEstimatedBudget: "",
    totalPages: "",
    openingDate: "",
    signingDay: today.getDate().toString(),
    signingMonth: (today.getMonth() + 1).toString(),
    signingYear: today.getFullYear().toString(),
    preparerName: "",
    chiefAccountantName: "",
    legalRepresentativeName: "",
  });

  // --- QUẢN LÝ STATE SỐ LIỆU ---
  const [openingBalance, setOpeningBalance] = useState(createCostObject());
  const [localItems, setLocalItems] = useState(defaultItems);
  const [cumulativeQuarter, setCumulativeQuarter] = useState(createCostObject());
  const [cumulativeYear, setCumulativeYear] = useState(createCostObject());
  const [cumulativeFromStart, setCumulativeFromStart] = useState(createCostObject());

  // Đồng bộ với formik values khi load API
  useEffect(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      setLocalItems(
        values.items.map((item) => ({
          ...item,
          costs: item.costs || createCostObject(),
        }))
      );
    }
    if (values.openingBalance)
      setOpeningBalance({ ...createCostObject(), ...values.openingBalance });
    if (values.cumulativeQuarter)
      setCumulativeQuarter({ ...createCostObject(), ...values.cumulativeQuarter });
    if (values.cumulativeYear)
      setCumulativeYear({ ...createCostObject(), ...values.cumulativeYear });
    if (values.cumulativeFromStart)
      setCumulativeFromStart({ ...createCostObject(), ...values.cumulativeFromStart });
    // Force sync cumulative to values if backend has
    if (onChangeValue) {
      onChangeValue("cumulativeQuarter", values.cumulativeQuarter || createCostObject());
      onChangeValue("cumulativeYear", values.cumulativeYear || createCostObject());
      onChangeValue("cumulativeFromStart", values.cumulativeFromStart || createCostObject());
    }

    setHeaderInfo((prev) => ({ ...prev, ...values }));
  }, [values]);

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) onChangeValue(field, val);
  };

  // Logic tính tổng ngang: Thiết bị Tổng = Cần lắp + Không cần lắp; Tổng phát sinh = XL + TB + CC + Khác
  const autoCalcHorizontal = (obj) => {
    const num = (v) => (v && !isNaN(v) ? parseFloat(v) : 0);
    const eqTotal = num(obj.equipmentRequireInstallation) + num(obj.equipmentNoInstallation);
    const total =
      num(obj.constructionAmount) + eqTotal + num(obj.toolsAmount) + num(obj.otherAmount);
    return { ...obj, equipmentTotal: eqTotal || "", totalAmount: total || "" };
  };

  const handleRowChange = (setter, field, value) => {
    setter((prev) => {
      const updated = { ...prev, [field]: value };
      return autoCalcHorizontal(updated);
    });
  };

  const handleChangeItem = (index, field, value) => {
    const newItems = [...localItems];
    const targetField = field.split(".");
    if (targetField.length === 2 && targetField[0] === "costs") {
      newItems[index].costs = {
        ...(newItems[index].costs || createCostObject()),
        [targetField[1]]: value,
      };
      newItems[index].costs = autoCalcHorizontal(newItems[index].costs);
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setLocalItems(newItems);
  };

  const addRow = () => setLocalItems([...localItems, createItem()]);
  const removeRow = (index) => {
    if (localItems.length <= 1) return;
    setLocalItems(localItems.filter((_, i) => i !== index));
  };

  // --- LOGIC TÍNH TOÁN DỌC (TỰ ĐỘNG) ---
  const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

  const calculatedTotalArising = useMemo(() => {
    const sum = (field) => localItems.reduce((acc, item) => acc + num(item.costs?.[field]), 0);
    return {
      totalAmount: sum("totalAmount"),
      constructionAmount: sum("constructionAmount"),
      equipmentTotal: sum("equipmentTotal"),
      equipmentRequireInstallation: sum("equipmentRequireInstallation"),
      equipmentNoInstallation: sum("equipmentNoInstallation"),
      toolsAmount: sum("toolsAmount"),
      otherAmount: sum("otherAmount"),
    };
  }, [localItems]);

  const calculatedClosing = useMemo(() => {
    const calc = (field) => num(openingBalance[field]) + calculatedTotalArising[field];
    return {
      totalAmount: calc("totalAmount"),
      constructionAmount: calc("constructionAmount"),
      equipmentTotal: calc("equipmentTotal"),
      equipmentRequireInstallation: calc("equipmentRequireInstallation"),
      equipmentNoInstallation: calc("equipmentNoInstallation"),
      toolsAmount: calc("toolsAmount"),
      otherAmount: calc("otherAmount"),
    };
  }, [openingBalance, calculatedTotalArising]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("openingBalance", openingBalance);
      onChangeValue("totalArising", calculatedTotalArising);
      onChangeValue("closingBalance", calculatedClosing);
      onChangeValue("cumulativeQuarter", cumulativeQuarter);
      onChangeValue("cumulativeYear", cumulativeYear);
      onChangeValue("cumulativeFromStart", cumulativeFromStart);
    }
  }, [
    localItems,
    openingBalance,
    calculatedTotalArising,
    calculatedClosing,
    cumulativeQuarter,
    cumulativeYear,
    cumulativeFromStart,
  ]);

  // --- STYLES ---
  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "13px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "13px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "40px",
      fontSize: "13px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "10px 0 20px 0" },
    titleMain: { fontSize: "16px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    projectInfo: { width: "70%", margin: "0 auto", lineHeight: "1.8" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "12px", marginTop: "15px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px", verticalAlign: "middle" },
    tdText: { border: "1px solid #000", padding: "0 4px", height: "30px", verticalAlign: "middle" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "12px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "12px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 2px",
      display: "block",
      color: "#000",
    },
    inputTableLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "12px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 4px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "30px",
    },
    rowBg: { backgroundColor: "#fafafa", fontWeight: "bold" },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  // Render helper cho các row Input
  const renderCostRow = (label, stateObj, setter, isAutoTotal = false, isBold = false) => (
    <tr style={isBold ? s.rowBg : {}}>
      <td style={s.td} colSpan={4}></td>
      <td style={s.tdText}>{label}</td>
      <td style={s.td}>
        <input
          disabled={containView || isAutoTotal}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.totalAmount || ""}
          onChange={(e) => handleRowChange(setter, "totalAmount", e.target.value)}
        />
      </td>
      <td style={s.td}>
        <input
          disabled={containView}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.constructionAmount || ""}
          onChange={(e) => handleRowChange(setter, "constructionAmount", e.target.value)}
        />
      </td>
      <td style={s.td}>
        <input
          disabled={containView || isAutoTotal}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.equipmentTotal || ""}
          onChange={(e) => handleRowChange(setter, "equipmentTotal", e.target.value)}
        />
      </td>
      <td style={s.td}>
        <input
          disabled={containView}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.equipmentRequireInstallation || ""}
          onChange={(e) => handleRowChange(setter, "equipmentRequireInstallation", e.target.value)}
        />
      </td>
      <td style={s.td}>
        <input
          disabled={containView}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.equipmentNoInstallation || ""}
          onChange={(e) => handleRowChange(setter, "equipmentNoInstallation", e.target.value)}
        />
      </td>
      <td style={s.td}>
        <input
          disabled={containView}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.toolsAmount || ""}
          onChange={(e) => handleRowChange(setter, "toolsAmount", e.target.value)}
        />
      </td>
      <td style={s.td}>
        <input
          disabled={containView}
          type="number"
          style={{ ...s.inputTable, fontWeight: isBold ? "bold" : "normal" }}
          value={stateObj.otherAmount || ""}
          onChange={(e) => handleRowChange(setter, "otherAmount", e.target.value)}
        />
      </td>
      <td style={s.td}></td>
      {!containView && <td style={s.td}></td>}
    </tr>
  );

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "40%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.company}
              onChange={(e) => handleHeaderChange("company", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Địa chỉ:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.companyAddress}
              onChange={(e) => handleHeaderChange("companyAddress", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "60%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S52-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE & PROJECT INFO */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>SỔ CHI PHÍ ĐẦU TƯ XÂY DỰNG</h2>
        <div style={{ fontWeight: "bold", marginTop: 5 }}>
          Tài khoản 241 - Xây dựng cơ bản dở dang
        </div>
        <div style={{ fontWeight: "bold", marginTop: 5, fontStyle: "italic" }}>
          Năm:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "60px", fontWeight: "bold" }}
            value={headerInfo.year}
            onChange={(e) => handleHeaderChange("year", e.target.value)}
          />
        </div>
      </div>

      <div style={s.projectInfo}>
        <div style={{ fontWeight: "bold" }}>
          Dự án:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "400px" }}
            value={headerInfo.projectName}
            onChange={(e) => handleHeaderChange("projectName", e.target.value)}
          />
        </div>
        <div style={{ fontWeight: "bold" }}>
          Công trình (HMCT):{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "320px" }}
            value={headerInfo.constructionName}
            onChange={(e) => handleHeaderChange("constructionName", e.target.value)}
          />
        </div>
        <div style={{ fontWeight: "bold" }}>
          Khởi công...{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "250px" }}
            value={headerInfo.startDate}
            onChange={(e) => handleHeaderChange("startDate", e.target.value)}
          />
        </div>
        <div style={{ fontWeight: "bold" }}>
          Hoàn thành...{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "250px" }}
            value={headerInfo.completionDate}
            onChange={(e) => handleHeaderChange("completionDate", e.target.value)}
          />
        </div>
        <div style={{ fontWeight: "bold" }}>
          Tổng dự toán:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "300px" }}
            type="number"
            value={headerInfo.totalEstimatedBudget}
            onChange={(e) => handleHeaderChange("totalEstimatedBudget", e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={3} style={{ ...s.th, width: "60px" }}>
                Ngày,
                <br />
                tháng
                <br />
                ghi sổ
              </th>
              <th colSpan={2} style={s.th}>
                Chứng từ
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "150px" }}>
                Diễn giải
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "50px" }}>
                Tài
                <br />
                khoản
                <br />
                đối ứng
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "80px" }}>
                Tổng
                <br />
                số
                <br />
                phát
                <br />
                sinh
              </th>
              <th colSpan={6} style={s.th}>
                Nội dung chi phí
              </th>
              <th rowSpan={3} style={{ ...s.th, width: "80px" }}>
                Ghi
                <br />
                chú
              </th>
              {!containView && (
                <th rowSpan={3} style={{ ...s.th, width: "30px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Số
                <br />
                hiệu
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Ngày,
                <br />
                tháng
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Xây
                <br />
                lắp
              </th>
              <th colSpan={3} style={s.th}>
                Thiết bị
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Công
                <br />
                cụ,
                <br />
                dụng
                <br />
                cụ
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Khác
              </th>
            </tr>
            <tr>
              <th style={{ ...s.th, width: "80px" }}>
                Tổng
                <br />
                số
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                TB
                <br />
                cần
                <br />
                lắp
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                TB
                <br />
                không
                <br />
                cần lắp
              </th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>F</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {/* 1. SỐ DƯ ĐẦU KỲ */}
            {renderCostRow("1 - Số dư đầu kỳ", openingBalance, setOpeningBalance, true, true)}

            {/* LABEL PHÁT SINH */}
            <tr>
              <td style={s.td} colSpan={4}></td>
              <td style={s.tdText} className="bold">
                2 - Số phát sinh trong kỳ
              </td>
              <td colSpan={7} style={s.td}></td>
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* DÒNG PHÁT SINH (ITEMS) */}
            {localItems.map((row, i) => (
              <tr key={i}>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.recordingDate || ""}
                    onChange={(e) => handleChangeItem(i, "recordingDate", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentNumber || ""}
                    onChange={(e) => handleChangeItem(i, "documentNumber", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentDate || ""}
                    onChange={(e) => handleChangeItem(i, "documentDate", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.description || ""}
                    onChange={(e) => handleChangeItem(i, "description", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.correspondingAccount || ""}
                    onChange={(e) => handleChangeItem(i, "correspondingAccount", e.target.value)}
                  />
                </td>

                {/* Cost Columns */}
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.totalAmount || ""}
                    onChange={(e) => handleChangeItem(i, "costs.totalAmount", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.constructionAmount || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "costs.constructionAmount", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.equipmentTotal || ""}
                    onChange={(e) => handleChangeItem(i, "costs.equipmentTotal", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.equipmentRequireInstallation || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "costs.equipmentRequireInstallation", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.equipmentNoInstallation || ""}
                    onChange={(e) =>
                      handleChangeItem(i, "costs.equipmentNoInstallation", e.target.value)
                    }
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.toolsAmount || ""}
                    onChange={(e) => handleChangeItem(i, "costs.toolsAmount", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.costs?.otherAmount || ""}
                    onChange={(e) => handleChangeItem(i, "costs.otherAmount", e.target.value)}
                  />
                </td>

                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.note || ""}
                    onChange={(e) => handleChangeItem(i, "note", e.target.value)}
                  />
                </td>
                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* CỘNG SỐ PHÁT SINH */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={4}></td>
              <td style={s.tdText}>Cộng số phát sinh</td>
              {[
                "totalAmount",
                "constructionAmount",
                "equipmentTotal",
                "equipmentRequireInstallation",
                "equipmentNoInstallation",
                "toolsAmount",
                "otherAmount",
              ].map((field) => (
                <td key={field} style={s.td}>
                  <span style={s.totalText}>{fmt(calculatedTotalArising[field])}</span>
                </td>
              ))}
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* 3. SỐ DƯ CUỐI KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td} colSpan={4}></td>
              <td style={s.tdText}>3 - Số dư cuối kỳ</td>
              {[
                "totalAmount",
                "constructionAmount",
                "equipmentTotal",
                "equipmentRequireInstallation",
                "equipmentNoInstallation",
                "toolsAmount",
                "otherAmount",
              ].map((field) => (
                <td key={field} style={s.td}>
                  <span style={s.totalText}>{fmt(calculatedClosing[field])}</span>
                </td>
              ))}
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* CÁC DÒNG LŨY KẾ */}
            {renderCostRow(
              "4 - Cộng lũy kế phát sinh quý",
              cumulativeQuarter,
              setCumulativeQuarter,
              true
            )}
            {renderCostRow(
              "5 - Cộng lũy kế phát sinh từ đầu năm",
              cumulativeYear,
              setCumulativeYear,
              true
            )}
            {renderCostRow(
              "6 - Cộng lũy kế phát sinh từ khởi công",
              cumulativeFromStart,
              setCumulativeFromStart,
              true
            )}
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={3} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              Thêm dòng
            </SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER INFO */}
      <div style={{ marginTop: "20px", lineHeight: "1.8" }}>
        <div>
          - Sổ này có{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "40px" }}
            value={headerInfo.totalPages}
            onChange={(e) => handleHeaderChange("totalPages", e.target.value)}
          />{" "}
          trang, đánh số từ trang 01 đến trang...
        </div>
        <div>
          - Ngày mở sổ:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.openingDate}
            onChange={(e) => handleHeaderChange("openingDate", e.target.value)}
          />
        </div>
      </div>

      {/* FOOTER CHỮ KÝ */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "33%" }}>
          <b>Người ghi sổ</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.preparerName}
            onChange={(e) => handleHeaderChange("preparerName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <b>Kế toán trưởng</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.chiefAccountantName}
            onChange={(e) => handleHeaderChange("chiefAccountantName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingDay}
              onChange={(e) => handleHeaderChange("signingDay", e.target.value)}
            />
            tháng
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingMonth}
              onChange={(e) => handleHeaderChange("signingMonth", e.target.value)}
            />
            năm
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, width: "40px", marginLeft: 4 }}
              value={headerInfo.signingYear}
              onChange={(e) => handleHeaderChange("signingYear", e.target.value)}
            />
          </div>
          <b>Người đại diện theo pháp luật</b>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "45px" }}>
            (Ký, họ tên, đóng dấu)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.legalRepresentativeName}
            onChange={(e) => handleHeaderChange("legalRepresentativeName", e.target.value)}
          />
        </div>
      </div> */}
    </SoftBox>
  );
}

ProjectCostLedgerInfo.propTypes = {
  formData: PropTypes.object,
};

export default ProjectCostLedgerInfo;
